<script setup lang="ts">
import { computed, useTemplateRef, ref, watch, nextTick } from 'vue';
import OverlayPanel from 'primevue/overlaypanel';
import { capitalizeStr, convertToTitleCase } from '@Helpers';

const props = defineProps<{
  field: any;
  rowData: any;
  settings: any;
}>();

const extraInfoPanel = useTemplateRef<InstanceType<typeof OverlayPanel> | null>('extraInfoPanel');
const textElement = useTemplateRef<HTMLParagraphElement | null>('textElement');

const isHovering = ref(false);
const isOverflowing = ref(false);

// NOTE :: The "see more" panel shows the raw value as plain text, never as HTML: values are typed by people
// (report names, notes), so rendering them as HTML would let one run script in another user's browser.
const panelText = computed(() => (Array.isArray(props.field) ? props.field.join(', ') : String(props.field ?? '')));
const showLength = computed(() => props.settings?.showLength);
const sanitizedField = computed((): any =>
  props.settings?.render?.targetKey.length ? getTargetValue(props.settings.render.targetKey) : props.field
);
const sanitizedFieldsWithoutSnakeCase = computed(() => {
  if (props.settings?.preserveCase) return sanitizedField.value;

  const isEmail = typeof sanitizedField.value === 'string' && sanitizedField.value.includes('@');
  if (isEmail) return sanitizedField.value;

  return /^[a-z]+_[a-z]+$/.test(sanitizedField.value) || /^[a-z]+_[a-z]+_[a-z]+$/.test(sanitizedField.value)
    ? convertToTitleCase(sanitizedField.value)
    : capitalizeStr(sanitizedField.value);
});

watch(() => props.field, checkOverflow, { immediate: true });

function getTargetValue(targetKeys: string[]) {
  let targetValue = props.rowData;

  targetKeys.forEach((key) => (targetValue = targetValue[key]));

  return targetValue;
}

function checkOverflow() {
  nextTick(() => {
    if (!textElement.value) return;

    isOverflowing.value = textElement.value.scrollWidth > textElement.value.clientWidth;
  });
}

function showPanel(event: any) {
  if (!isOverflowing.value) return;

  isHovering.value = true;
  extraInfoPanel.value?.show(event);
}

function hidePanel() {
  setTimeout(() => (!isHovering.value ? extraInfoPanel.value?.hide() : ''), 100);
}
</script>

<template>
  <div
    v-if="field"
    :class="[
      'flex items-center gap-2 overflow-hidden truncate text-ellipsis whitespace-nowrap',
      isOverflowing ? 'justify-between' : '',
    ]"
    @mouseenter="showPanel"
    @mouseleave="
      isHovering = false;
      hidePanel();
    "
  >
    <p
      v-if="Array.isArray(sanitizedField) && showLength"
      ref="textElement"
    >
      {{ sanitizedField.length ? sanitizedField.length : '-' }}
    </p>

    <p
      v-else-if="Array.isArray(sanitizedField)"
      ref="textElement"
    >
      {{ sanitizedField.length ? sanitizedField.join(', ') : '-' }}
    </p>

    <p
      v-else-if="typeof sanitizedField === 'object' && sanitizedField !== null && !Array.isArray(sanitizedField)"
      ref="textElement"
    >
      {{ sanitizedField.value !== undefined ? sanitizedField.value : '-' }}
    </p>

    <p
      v-else
      class="overflow-hidden text-ellipsis"
      ref="textElement"
    >
      {{ sanitizedField ? sanitizedFieldsWithoutSnakeCase : '-' }}
    </p>

    <i
      v-if="isOverflowing"
      class="pi pi-eye text-sm text-slate-500"
    ></i>

    <OverlayPanel
      v-if="isOverflowing"
      ref="extraInfoPanel"
      @mouseenter="isHovering = true"
      @mouseleave="
        isHovering = false;
        hidePanel();
      "
    >
      <div class="flex gap-2 border-t py-1 text-sm first:border-t-0 first:pt-0 last:pb-0">
        <p class="max-h-[450px] max-w-[300px] overflow-auto whitespace-pre-line text-sm">{{ panelText }}</p>
      </div>
    </OverlayPanel>
  </div>

  <p v-else>{{ settings?.isNumber ? '0' : '-' }}</p>
</template>
